import React, { useState, useContext, useCallback, useRef, useEffect, Suspense, lazy, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxon3Plugin from '@fullcalendar/luxon3';
import { DateTime } from 'luxon';
import { UserContext } from '../contexts/UserContext';
import { HOME_TIMEZONE } from '../utils/timezone';
import { api as apiClient } from '../services/api';
import { getCachedEffectiveLimit } from '../utils/limitSettings';
import {
  buildGuestEventPayloadFromCalendarEvent,
  getGuestEventApiErrorCode,
  updateGuestEvent as updateGuestEventRequest
} from '../services/guestEventApi';
import { toCalendarEvent } from './Calendar/utils/calendarHelpers';
import {
  customFrLocale,
  customKoLocale,
  customEnLocale,
  getCalendarLocale
} from './Calendar/utils/localeConfig';
import {
  CalendarMonthEventCircle,
  CalendarTimeGridEventContent
} from './Calendar/components/EventContent';
import { getGuestEventApiErrorMessage, getGuestMessage } from './guestEventMessages';
import { getGuestCalendarInitialDate } from './guestCalendarDateUtils';
import {
  buildGuestCapacityFullEvents,
  findGuestCapacityConflict,
  GUEST_MAX_CONCURRENT_EVENTS,
  hasOwnGuestEventOverlap,
  isGuestCapacityFullEvent,
} from './guestCalendarLimitUtils';
// Calendar.css 스타일 재사용 (GNEGNEGNE 캘린더와 동일)
import './Calendar.css';
import styles from './GuestCalendar.module.css';

const GuestAddEventModal = lazy(() => import('./GuestAddEventModal'));
const GuestEditEventModal = lazy(() => import('./GuestEditEventModal'));

// 다국어 텍스트
const TEXT = {
  guestPeriod: {
    ko: '게스트 기간',
    en: 'Guest Period',
    fr: 'Période invité'
  },
  today: { ko: '오늘', en: 'Today', fr: "Auj." },
  month: { ko: '월', en: 'M.', fr: 'M.' },
  week: { ko: '주', en: 'W.', fr: 'S.' },
  day: { ko: '일', en: 'D.', fr: 'J.' },
  expandEarlyHours: { ko: '0-8', en: '0-8', fr: '0-8' },
  collapseEarlyHours: { ko: '0-8', en: '0-8', fr: '0-8' },
  eventMoveFailed: {
    ko: '이벤트 이동에 실패했습니다.',
    en: 'Failed to move event.',
    fr: "Échec du déplacement de l'événement."
  },
  eventResizeFailed: {
    ko: '이벤트 크기 조절에 실패했습니다.',
    en: 'Failed to resize event.',
    fr: "Échec du redimensionnement de l'événement."
  }
};

const MODAL_LOADING_TEXT = {
  ko: '모달 불러오는 중...',
  en: 'Loading modal...',
  fr: 'Chargement...'
};

const GuestCalendarModalFallback = ({ lang }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(12, 12, 12, 0.18)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000001,
      pointerEvents: 'none',
    }}
  >
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.94)',
        color: '#111',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: '14px',
        padding: '14px 18px',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
        fontSize: '14px',
        fontWeight: 600,
      }}
    >
      {MODAL_LOADING_TEXT[lang] || MODAL_LOADING_TEXT.ko}
    </div>
  </div>
);

const DEFAULT_TIMEZONE = HOME_TIMEZONE;

const getGuestOverlapMessage = (lang) => getGuestMessage(lang, 'overlap');
const getGuestCapacityMessage = (lang) => getGuestMessage(lang, 'capacity');

const getGuestUpdateFailedMessage = (lang, errorCode) => {
  return getGuestEventApiErrorMessage(lang, errorCode, null, {
    outsideGuestPeriodKey: 'outsideGuestPeriodUpdate'
  });
};

const isTimeGridView = (viewType) => viewType === 'timeGridWeek' || viewType === 'timeGridDay';

/**
 * GuestCalendar - 게스트 아티스트 캘린더
 * PersonalCalendar와 동일한 FullCalendar 스타일 사용
 */
export default function GuestCalendar({ guestStartDate, guestEndDate }) {
  const { userInfo } = useContext(UserContext);
  const lang = userInfo?.preferred_language || 'fr';
  const guestName = userInfo?.user_name || '';
  const guestProfileId = userInfo?.guest_profile?.id;
  const guestUserId = userInfo?.user_id;
  const calendarRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const dashedLinesTimeoutRef = useRef(null);
  const capacityRefreshTimeoutRef = useRef(null);
  const capacityRefreshSeqRef = useRef(0);
  const capacityFullEventsSignatureRef = useRef('');
  const capacitySourceSignatureRef = useRef('');

  // 게스트 기간 계산 (Paris 시간대 기준)
  const guestStart = guestStartDate
    ? DateTime.fromISO(guestStartDate).setZone(DEFAULT_TIMEZONE).startOf('day')
    : DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day');
  const guestEnd = guestEndDate
    ? DateTime.fromISO(guestEndDate).setZone(DEFAULT_TIMEZONE).endOf('day')
    : DateTime.now().setZone(DEFAULT_TIMEZONE).plus({ months: 3 }).endOf('day');
  const initialCalendarDate = getGuestCalendarInitialDate({
    guestStart,
    guestEnd,
    timezone: DEFAULT_TIMEZONE
  });

  // 뷰 상태
  const [currentViewType, setCurrentViewType] = useState('timeGridWeek');
  const [calendarTitle, setCalendarTitle] = useState('');

  // 모달 상태
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [capacityFullEvents, setCapacityFullEvents] = useState([]);
  const guestDayOffDatesRef = useRef([]);

  const getCalendarApi = useCallback(() => {
    return calendarRef.current?.getApi() || null;
  }, []);

  const getCalendarElement = useCallback(() => {
    return calendarWrapperRef.current?.querySelector('.fc') || null;
  }, []);

  const clearScheduledDashedLinesUpdate = useCallback(() => {
    if (dashedLinesTimeoutRef.current) {
      clearTimeout(dashedLinesTimeoutRef.current);
      dashedLinesTimeoutRef.current = null;
    }
  }, []);

  const clearScheduledCapacityRefresh = useCallback(() => {
    if (capacityRefreshTimeoutRef.current) {
      clearTimeout(capacityRefreshTimeoutRef.current);
      capacityRefreshTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearScheduledDashedLinesUpdate();
      clearScheduledCapacityRefresh();
    };
  }, [clearScheduledCapacityRefresh, clearScheduledDashedLinesUpdate]);

  const applyDashedLines = useCallback(() => {
    const calendarElement = getCalendarElement();
    if (!calendarElement) {
      return;
    }

    calendarElement.querySelectorAll('.dashed-time-line').forEach((line) => line.remove());

    calendarElement.querySelectorAll('.fc-timegrid-slot').forEach((slot) => {
      const dashedLine = document.createElement('div');
      dashedLine.className = 'dashed-time-line';
      slot.style.position = 'relative';
      slot.appendChild(dashedLine);
    });
  }, [getCalendarElement]);

  const updateCapacityFullEvents = useCallback((nextEvents) => {
    const nextSignature = nextEvents
      .map((event) => `${event.id}:${event.start}:${event.end}:${event.extendedProps?.limit || ''}:${event.extendedProps?.currentCount || ''}`)
      .join('|');

    if (capacityFullEventsSignatureRef.current === nextSignature) {
      return;
    }

    capacityFullEventsSignatureRef.current = nextSignature;
    setCapacityFullEvents(nextEvents);
  }, []);

  const refreshCapacityFullEvents = useCallback(async (events = null) => {
    const calendarApi = getCalendarApi();
    const view = calendarApi?.view;
    const refreshSeq = capacityRefreshSeqRef.current + 1;
    capacityRefreshSeqRef.current = refreshSeq;

    if (!view || !isTimeGridView(view.type)) {
      capacitySourceSignatureRef.current = '';
      updateCapacityFullEvents([]);
      return;
    }

    const sourceEvents = (events || calendarApi.getEvents()).filter((event) => !isGuestCapacityFullEvent(event));
    const viewStart = view.activeStart || view.currentStart;
    const viewEnd = view.activeEnd || view.currentEnd;
    const sourceSignature = [
      view.type,
      viewStart?.toISOString?.() || String(viewStart),
      viewEnd?.toISOString?.() || String(viewEnd),
      ...sourceEvents.map((event) => `${event.id}:${event.start?.getTime?.() || event.startStr || ''}:${event.end?.getTime?.() || event.endStr || ''}`)
    ].join('|');

    if (capacitySourceSignatureRef.current === sourceSignature) {
      return;
    }

    capacitySourceSignatureRef.current = sourceSignature;

    const nextEvents = await buildGuestCapacityFullEvents({
      viewStart,
      viewEnd,
      events: sourceEvents,
      guestName,
      currentUserId: guestUserId,
      loadEffectiveLimit: getCachedEffectiveLimit,
      fallbackLimit: GUEST_MAX_CONCURRENT_EVENTS,
      timezone: DEFAULT_TIMEZONE
    });

    if (capacityRefreshSeqRef.current !== refreshSeq) {
      return;
    }

    updateCapacityFullEvents(nextEvents);
  }, [getCalendarApi, guestName, guestUserId, updateCapacityFullEvents]);

  const scheduleCapacityFullEventsRefresh = useCallback((events = null, delay = 120) => {
    clearScheduledCapacityRefresh();

    capacityRefreshTimeoutRef.current = setTimeout(() => {
      capacityRefreshTimeoutRef.current = null;
      refreshCapacityFullEvents(events);
    }, delay);
  }, [clearScheduledCapacityRefresh, refreshCapacityFullEvents]);

  const scheduleDashedLinesUpdate = useCallback((delay = 220) => {
    clearScheduledDashedLinesUpdate();

    dashedLinesTimeoutRef.current = setTimeout(() => {
      dashedLinesTimeoutRef.current = null;
      applyDashedLines();
    }, delay);
  }, [applyDashedLines, clearScheduledDashedLinesUpdate]);

  // 노치 배경색을 페이지 배경색과 일치시킴 (PWA)
  useEffect(() => {
    const backgroundColor = '#fffdf8';
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', backgroundColor);
    }
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = backgroundColor;

    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8');
      }
      document.body.style.backgroundColor = originalBodyBg;
    };
  }, []);

  // 게스트 기간 외 날짜 스타일을 위한 동적 CSS 삽입
  useEffect(() => {
    const styleId = 'guest-calendar-period-styles';
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    styleEl.innerHTML = `
      /* 게스트 기간 외 날짜 스타일 */
      .guest-calendar-wrapper .outside-guest-period {
        background-color: rgba(210, 210, 210, 0.4) !important;
        cursor: not-allowed !important;
      }
      
      .guest-calendar-wrapper .outside-guest-period-header {
        opacity: 0.5 !important;
        background-color: rgba(210, 210, 210, 0.3) !important;
      }
      
      /* 월간 뷰 셀 스타일 오버라이드 */
      .guest-calendar-wrapper .fc-daygrid-day.outside-guest-period {
        background-color: rgba(210, 210, 210, 0.4) !important;
        box-shadow: none !important;
      }
      
      /* 비활성화 날짜의 이벤트 완전히 숨기기 */
      .guest-calendar-wrapper .fc-timegrid-col.outside-guest-period .fc-timegrid-event,
      .guest-calendar-wrapper .fc-timegrid-col.outside-guest-period .fc-event,
      .guest-calendar-wrapper .fc-daygrid-day.outside-guest-period .fc-event,
      .guest-calendar-wrapper .fc-daygrid-day.outside-guest-period .fc-daygrid-event {
        display: none !important;
      }
      
      /* 비활성화 날짜의 하루종일 이벤트도 숨기기 */
      .guest-calendar-wrapper .fc-daygrid-day.outside-guest-period .fc-daygrid-day-events {
        visibility: hidden !important;
      }
      
      /* 배경 이벤트(홈 스튜디오) 텍스트 스타일 - 얇은 굵기 */
      .guest-calendar-wrapper .fc-bg-event,
      .guest-calendar-wrapper .fc-bg-event .fc-event-title,
      .guest-calendar-wrapper .fc-bg-event .fc-event-main {
        font-weight: 300 !important;
        font-size: 12px !important;
        color: rgba(100, 100, 100, 0.6) !important;
        pointer-events: none !important;
        cursor: default !important;
      }
      
      /* 배경 이벤트 호버 효과 비활성화 */
      .guest-calendar-wrapper .fc-bg-event:hover {
        cursor: default !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      /* 10시 이전과 19시 이후 시간대 비활성화 */
      .guest-calendar-wrapper .fc-timegrid-slot.outside-work-hours,
      .guest-calendar-wrapper .fc-timegrid-slot-lane.outside-work-hours {
        background-color: rgba(220, 220, 220, 0.25) !important;
      }
    `;

    return () => {
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    };
  }, []);

  // 뷰 범위가 게스트 기간과 겹치는지 확인하는 헬퍼 함수
  const viewOverlapsGuestPeriod = useCallback((viewStart, viewEnd) => {
    const viewStartDT = DateTime.fromJSDate(viewStart).setZone(DEFAULT_TIMEZONE).startOf('day');
    const viewEndDT = DateTime.fromJSDate(viewEnd).setZone(DEFAULT_TIMEZONE).endOf('day');

    // 두 기간이 겹치는지 확인: !(viewEnd < guestStart || viewStart > guestEnd)
    return !(viewEndDT < guestStart || viewStartDT > guestEnd);
  }, [guestStart, guestEnd]);

  // 캘린더 네비게이션 핸들러
  const handlePrev = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      // 이전으로 이동 시 예상되는 뷰 범위 계산
      const currentView = api.view;
      const viewDuration = currentView.currentEnd.getTime() - currentView.currentStart.getTime();
      const prevStart = new Date(currentView.currentStart.getTime() - viewDuration);
      const prevEnd = new Date(currentView.currentEnd.getTime() - viewDuration);

      // 이전 뷰가 게스트 기간과 겹치는지 확인
      if (viewOverlapsGuestPeriod(prevStart, prevEnd)) {
        api.prev();
      }
      // 겹치지 않으면 이동하지 않음 (아무 동작 안 함)
    }
  }, [viewOverlapsGuestPeriod]);

  const handleNext = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      // 다음으로 이동 시 예상되는 뷰 범위 계산
      const currentView = api.view;
      const viewDuration = currentView.currentEnd.getTime() - currentView.currentStart.getTime();
      const nextStart = new Date(currentView.currentStart.getTime() + viewDuration);
      const nextEnd = new Date(currentView.currentEnd.getTime() + viewDuration);

      // 다음 뷰가 게스트 기간과 겹치는지 확인
      if (viewOverlapsGuestPeriod(nextStart, nextEnd)) {
        api.next();
      }
      // 겹치지 않으면 이동하지 않음
    }
  }, [viewOverlapsGuestPeriod]);

  const handleToday = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      const targetDate = getGuestCalendarInitialDate({
        guestStart,
        guestEnd,
        timezone: DEFAULT_TIMEZONE
      });

      api.gotoDate(targetDate.toJSDate());
    }
  }, [guestStart, guestEnd]);

  const handleChangeView = useCallback((viewType) => {
    const api = getCalendarApi();
    if (api) {
      api.changeView(viewType);
      setCurrentViewType(viewType);
      setCalendarTitle(api.view.title);
    }
  }, [getCalendarApi]);

  const getDateKeyInParis = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const parsed = value.includes('T')
        ? DateTime.fromJSDate(new Date(value)).setZone(DEFAULT_TIMEZONE)
        : DateTime.fromISO(value, { zone: DEFAULT_TIMEZONE });
      return parsed.isValid ? parsed.setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd') : null;
    }

    return DateTime.fromJSDate(value).setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
  }, []);

  const getInclusiveEndDateKeyInParis = useCallback((endValue, startValue) => {
    if (!endValue) {
      return getDateKeyInParis(startValue);
    }

    const startDateTime = typeof startValue === 'string'
      ? (
        startValue.includes('T')
          ? DateTime.fromJSDate(new Date(startValue)).setZone(DEFAULT_TIMEZONE)
          : DateTime.fromISO(startValue, { zone: DEFAULT_TIMEZONE })
      )
      : DateTime.fromJSDate(startValue).setZone(DEFAULT_TIMEZONE);
    const endDateTime = typeof endValue === 'string'
      ? (
        endValue.includes('T')
          ? DateTime.fromJSDate(new Date(endValue)).setZone(DEFAULT_TIMEZONE)
          : DateTime.fromISO(endValue, { zone: DEFAULT_TIMEZONE })
      )
      : DateTime.fromJSDate(endValue).setZone(DEFAULT_TIMEZONE);

    if (!endDateTime.isValid) {
      return null;
    }

    const adjustedEndDate = startDateTime?.isValid && endDateTime > startDateTime
      ? endDateTime.minus({ milliseconds: 1 })
      : endDateTime;

    return adjustedEndDate.setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
  }, [getDateKeyInParis]);

  const isGuestBlockedDay = useCallback((value) => {
    const dateKey = getDateKeyInParis(value);
    return dateKey ? guestDayOffDatesRef.current.includes(dateKey) : false;
  }, [getDateKeyInParis]);

  const hasGuestBlockedDayInRange = useCallback((startValue, endValue) => {
    const startDateKey = getDateKeyInParis(startValue);
    const endDateKey = getInclusiveEndDateKeyInParis(endValue, startValue);

    if (!startDateKey || !endDateKey) {
      return false;
    }

    return guestDayOffDatesRef.current.some((day) => day >= startDateKey && day <= endDateKey);
  }, [getDateKeyInParis, getInclusiveEndDateKeyInParis]);

  // 게스트 이벤트 fetch 함수
  const fetchGuestEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
    if (!guestProfileId) {
      successCallback([]);
      return;
    }

    const startISO = DateTime.fromJSDate(fetchInfo.start).setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
    // end 날짜에 1일 추가하여 마지막 날 이벤트도 포함되도록 함
    const endISO = DateTime.fromJSDate(fetchInfo.end).setZone(DEFAULT_TIMEZONE).plus({ days: 1 }).toFormat('yyyy-MM-dd');

    apiClient.get(`/guest-artists/${guestProfileId}/calendar`, {
      start: startISO,
      end: endISO,
    })
      .then(data => {
        const events = data.data?.events || data.events || [];
        const mappedEvents = events
          .map(event => {
            const calEvent = toCalendarEvent(event);
            if (calEvent) {
              calEvent.extendedProps = {
                ...calEvent.extendedProps,
                calendarType: 'guest',
                isGuestEvent: true
              };
            }
            return calEvent;
          })
          .filter(e => e !== null);
        successCallback(mappedEvents);
      })
      .catch(err => {
        failureCallback(err);
      });
  }, [guestProfileId]);

  const fetchGuestDayOffEvents = useCallback((fetchInfo, successCallback) => {
    if (!guestUserId) {
      guestDayOffDatesRef.current = [];
      successCallback([]);
      return;
    }

    const startISO = DateTime.fromJSDate(fetchInfo.start).setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
    const endISO = DateTime.fromJSDate(fetchInfo.end).setZone(DEFAULT_TIMEZONE).plus({ days: 1 }).toFormat('yyyy-MM-dd');

    apiClient.get('/artist-dayoff', {
      artist_id: guestUserId,
      start: startISO,
      end: endISO
    })
      .then((data) => {
        const dayOffs = Array.isArray(data) ? data : [];
        const dayOffDays = dayOffs
          .map((entry) => entry?.day)
          .filter(Boolean)
          .sort((left, right) => left.localeCompare(right));

        guestDayOffDatesRef.current = dayOffDays;

        const mappedEvents = dayOffDays.map((day) => ({
          id: `guest-dayoff-${day}`,
          title: 'DayOff',
          start: day,
          end: DateTime.fromISO(day, { zone: DEFAULT_TIMEZONE }).plus({ days: 1 }).toFormat('yyyy-MM-dd'),
          allDay: true,
          display: 'background',
          className: 'dayoff-bg-event',
          extendedProps: {
            isBackground: true,
            isGuestBlockedDay: true,
            dayOffDay: day
          }
        }));

        successCallback(mappedEvents);
      })
      .catch((error) => {
        console.error('Failed to load guest blocked days:', error);
        guestDayOffDatesRef.current = [];
        successCallback([]);
      });
  }, [guestUserId]);

  // 홈스튜디오 이벤트 fetch 함수 (배경 이벤트로 표시)
  const fetchHomeStudioEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
    if (!userInfo) {
      successCallback([]);
      return;
    }

    const startISO = new Date(fetchInfo.start).toISOString();
    const endISO = new Date(fetchInfo.end).toISOString();

    apiClient.get('/events', {
      start_time: startISO,
      end_time: endISO,
    })
      .then(data => {
        const events = data?.events || [];

        // 타인 이벤트만 필터링 (게스트 본인 이벤트 제외, 하루종일 이벤트 제외)
        const otherEvents = events.filter(event =>
          event.user_id !== userInfo?.user_id &&
          event.created_by !== userInfo?.user_id &&
          !event.is_all_day && !event.allDay  // 하루종일 이벤트 제외
        );

        const mappedEvents = otherEvents
          .map(event => {
            const calEvent = toCalendarEvent(event);
            if (calEvent) {
              // 일반 이벤트로 표시하되 회색 스타일 (겹침 방지)
              calEvent.title = 'RDV';  // 통일된 타이틀
              calEvent.display = 'auto';
              calEvent.backgroundColor = 'rgba(210, 210, 210, 0.5)';
              calEvent.borderColor = 'rgba(190, 190, 190, 0.6)';
              calEvent.textColor = '#888';
              calEvent.editable = false;
              calEvent.classNames = ['home-studio-bg-event'];
              calEvent.extendedProps = {
                ...calEvent.extendedProps,
                sourceType: 'homeStudioBackground',
                isBackground: true
              };
            }
            return calEvent;
          })
          .filter(e => e !== null);

        successCallback(mappedEvents);
      })
      .catch(() => {
        successCallback([]);
      });
  }, [userInfo]);

  const calendarEventSources = useMemo(() => ([
    {
      events: fetchHomeStudioEvents,
      id: 'homeStudioEvents'
    },
    {
      events: capacityFullEvents,
      id: 'guestCapacityFullEvents'
    },
    {
      events: fetchGuestDayOffEvents,
      id: 'guestDayOffEvents'
    },
    {
      events: fetchGuestEvents,
      id: 'guestEvents'
    }
  ]), [capacityFullEvents, fetchGuestDayOffEvents, fetchGuestEvents, fetchHomeStudioEvents]);

  const getCapacityConflict = useCallback(async (startTime, endTime, excludeEventId = null) => {
    const calendarApi = getCalendarApi();
    if (!calendarApi) {
      return null;
    }

    return findGuestCapacityConflict({
      startTime,
      endTime,
      events: calendarApi.getEvents(),
      guestName,
      excludeEventId,
      loadEffectiveLimit: getCachedEffectiveLimit,
      fallbackLimit: GUEST_MAX_CONCURRENT_EVENTS,
      timezone: DEFAULT_TIMEZONE
    });
  }, [getCalendarApi, guestName]);

  // 본인 이벤트와 겹치는지 확인
  const hasOwnEventOverlap = useCallback((startTime, endTime, excludeEventId = null) => {
    if (!calendarRef.current) return false;
    const calendarApi = calendarRef.current.getApi();
    return hasOwnGuestEventOverlap({
      events: calendarApi.getEvents(),
      startTime,
      endTime,
      currentUserId: guestUserId,
      excludeEventId,
      timezone: DEFAULT_TIMEZONE
    });
  }, [guestUserId]);

  // 날짜 클릭 핸들러
  const handleDateClick = useCallback(async (info) => {
    const clickedDate = DateTime.fromISO(info.dateStr.split('T')[0]).setZone(DEFAULT_TIMEZONE);

    // 게스트 기간 내인지 확인 (게스트 시작일 또는 오늘 중 더 이른 날부터 guestEnd까지 허용)
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day');
    const earliestDate = guestStart < today ? guestStart : today;
    if (clickedDate < earliestDate || clickedDate > guestEnd) {
      return; // 기간 외 클릭 무시
    }

    const dateStr = info.dateStr;
    const datePart = dateStr.split('T')[0];

    if (isGuestBlockedDay(datePart)) {
      alert(getGuestMessage(lang, 'blockedDay'));
      return;
    }

    // 시간대 클릭인지 확인
    if (dateStr.includes('T')) {
      const timePart = dateStr.split('T')[1].substring(0, 5);

      // 시간 검증: 10시 이전 또는 19시 이후는 클릭 차단
      const [hours] = timePart.split(':').map(Number);
      if (hours < 10 || hours >= 19) {
        return; // 영업 시간 외 클릭 무시
      }

      const [, minutes] = timePart.split(':').map(Number);
      const endMinutes = minutes + 30;
      const endHours = hours + Math.floor(endMinutes / 60);
      const endTimePart = `${String(endHours).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;

      const startISO = `${datePart}T${timePart}:00`;
      const endISO = `${datePart}T${endTimePart}:00`;

      if (hasGuestBlockedDayInRange(startISO, endISO)) {
        alert(getGuestMessage(lang, 'blockedDay'));
        return;
      }

      // 본인 이벤트와 겹치면 차단
      if (hasOwnEventOverlap(startISO, endISO)) {
        alert(getGuestOverlapMessage(lang));
        return;
      }

      const capacityConflict = await getCapacityConflict(startISO, endISO);
      if (capacityConflict) {
        alert(getGuestCapacityMessage(lang));
        return;
      }

      setSelectedDate(datePart);
      setSelectedStartTime(timePart);
      setSelectedEndDate(datePart);
      setSelectedEndTime(endTimePart);
    } else {
      setSelectedDate(dateStr);
      setSelectedStartTime(null);
      setSelectedEndDate(dateStr);
      setSelectedEndTime(null);
    }
    setShowAddModal(true);
  }, [guestStart, guestEnd, getCapacityConflict, hasOwnEventOverlap, hasGuestBlockedDayInRange, isGuestBlockedDay, lang]);

  // 시간 범위 선택 핸들러
  const handleSelect = useCallback(async (info) => {
    const startDate = info.startStr.split('T')[0];
    const selectedStart = DateTime.fromISO(startDate).setZone(DEFAULT_TIMEZONE);

    // 게스트 기간 내인지 확인 (게스트 시작일 또는 오늘 중 더 이른 날부터 guestEnd까지 허용)
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day');
    const earliestDate = guestStart < today ? guestStart : today;
    if (selectedStart < earliestDate || selectedStart > guestEnd) {
      return;
    }

    if (hasGuestBlockedDayInRange(info.start, info.end)) {
      info.view.calendar.unselect();
      alert(getGuestMessage(lang, 'blockedDay'));
      return;
    }

    const startTimePart = info.startStr.includes('T')
      ? info.startStr.split('T')[1].substring(0, 5)
      : '10:00';

    // 시간 검증: 10시 이전 또는 19시 이후는 선택 차단
    const [startHour] = startTimePart.split(':').map(Number);
    if (startHour < 10 || startHour >= 19) {
      return; // 영업 시간 외 선택 무시
    }

    const endDate = info.endStr.split('T')[0];
    const endTimePart = info.endStr.includes('T')
      ? info.endStr.split('T')[1].substring(0, 5)
      : '11:00';

    // 해당 시간대에 겹치는 배경 이벤트가 3개 이상이면 차단
    const startISO = `${startDate}T${startTimePart}:00`;
    const endISO = `${endDate}T${endTimePart}:00`;

    // 본인 이벤트와 겹치면 차단
    if (hasOwnEventOverlap(startISO, endISO)) {
      alert(getGuestOverlapMessage(lang));
      info.view.calendar.unselect();
      return;
    }

    const capacityConflict = await getCapacityConflict(startISO, endISO);
    if (capacityConflict) {
      alert(getGuestCapacityMessage(lang));
      info.view.calendar.unselect();
      return;
    }

    setSelectedDate(startDate);
    setSelectedStartTime(startTimePart);
    setSelectedEndDate(endDate);
    setSelectedEndTime(endTimePart);
    setShowAddModal(true);
  }, [guestStart, guestEnd, getCapacityConflict, hasOwnEventOverlap, hasGuestBlockedDayInRange, lang]);

  // 이벤트 클릭 핸들러
  const handleEventClick = useCallback((info) => {
    // 배경 이벤트(스태프)는 클릭 무시
    if (info.event.extendedProps?.isBackground || info.event.display === 'background') {
      return;
    }

    const fcEvent = info.event;
    const plainEvent = {
      id: fcEvent.id,
      title: fcEvent.title,
      start: fcEvent.start?.toISOString() || fcEvent.startStr,
      end: fcEvent.end?.toISOString() || fcEvent.endStr,
      allDay: fcEvent.allDay,
      is_all_day: fcEvent.allDay,
      extendedProps: fcEvent.extendedProps || {}
    };

    setSelectedEvent(plainEvent);
    setShowEditModal(true);
  }, []);

  // 이벤트 추가 완료 핸들러
  const handleEventAdded = useCallback(() => {
    setShowAddModal(false);
    getCalendarApi()?.refetchEvents();
  }, [getCalendarApi]);

  // 이벤트 수정/삭제 완료 핸들러
  const handleEventUpdated = useCallback(() => {
    setShowEditModal(false);
    setSelectedEvent(null);
    getCalendarApi()?.refetchEvents();
  }, [getCalendarApi]);

  // 이벤트 드래그 이동 핸들러
  const updateGuestEvent = useCallback(async (guestEventId, event, fallbackMessage) => {
    try {
      await updateGuestEventRequest(
        guestEventId,
        event.id,
        buildGuestEventPayloadFromCalendarEvent(event, DEFAULT_TIMEZONE)
      );
    } catch (error) {
      const errorCode = getGuestEventApiErrorCode(error);
      const message = getGuestUpdateFailedMessage(lang, errorCode) || fallbackMessage;
      const normalizedError = new Error(message);
      normalizedError.code = errorCode;
      throw normalizedError;
    }
  }, [lang]);

  const handleEventDrop = useCallback(async (info) => {
    const event = info.event;
    const guestEventId = guestProfileId;

    // 게스트 기간 내인지 확인 (게스트 시작일 또는 오늘 중 더 이른 날부터 guestEnd까지 허용)
    const eventDate = DateTime.fromJSDate(event.start).setZone(DEFAULT_TIMEZONE);
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day');
    const earliestDate = guestStart < today ? guestStart : today;
    if (eventDate < earliestDate || eventDate > guestEnd) {
      info.revert();
      return;
    }

    const eventEnd = event.end || new Date(event.start.getTime() + 60 * 60 * 1000);
    if (!guestEventId || hasGuestBlockedDayInRange(event.start, eventEnd)) {
      info.revert();
      alert(getGuestMessage(lang, 'blockedDay'));
      return;
    }

    const movedStartISO = DateTime.fromJSDate(event.start).setZone(DEFAULT_TIMEZONE).toISO();
    const movedEndISO = DateTime.fromJSDate(eventEnd).setZone(DEFAULT_TIMEZONE).toISO();

    if (hasOwnEventOverlap(movedStartISO, movedEndISO, event.id)) {
      info.revert();
      alert(getGuestOverlapMessage(lang));
      return;
    }

    const capacityConflict = await getCapacityConflict(movedStartISO, movedEndISO, event.id);
    if (capacityConflict) {
      info.revert();
      alert(getGuestCapacityMessage(lang));
      return;
    }

    try {
      await updateGuestEvent(guestEventId, event, TEXT.eventMoveFailed[lang]);
    } catch (err) {
      info.revert();
      alert(err.message || TEXT.eventMoveFailed[lang]);
    }
  }, [getCapacityConflict, guestEnd, guestProfileId, guestStart, hasGuestBlockedDayInRange, hasOwnEventOverlap, lang, updateGuestEvent]);

  // 이벤트 리사이즈 핸들러
  const handleEventResize = useCallback(async (info) => {
    const event = info.event;
    const guestEventId = guestProfileId;

    if (!guestEventId || hasGuestBlockedDayInRange(event.start, event.end)) {
      info.revert();
      alert(getGuestMessage(lang, 'blockedDay'));
      return;
    }

    const resizedEnd = event.end || new Date(event.start.getTime() + 60 * 60 * 1000);
    const resizedStartISO = DateTime.fromJSDate(event.start).setZone(DEFAULT_TIMEZONE).toISO();
    const resizedEndISO = DateTime.fromJSDate(resizedEnd).setZone(DEFAULT_TIMEZONE).toISO();

    if (hasOwnEventOverlap(resizedStartISO, resizedEndISO, event.id)) {
      info.revert();
      alert(getGuestOverlapMessage(lang));
      return;
    }

    const capacityConflict = await getCapacityConflict(resizedStartISO, resizedEndISO, event.id);
    if (capacityConflict) {
      info.revert();
      alert(getGuestCapacityMessage(lang));
      return;
    }

    try {
      await updateGuestEvent(guestEventId, event, TEXT.eventResizeFailed[lang]);
    } catch (err) {
      info.revert();
      alert(err.message || TEXT.eventResizeFailed[lang]);
    }
  }, [getCapacityConflict, guestProfileId, hasGuestBlockedDayInRange, hasOwnEventOverlap, lang, updateGuestEvent]);

  // 뷰 변경 시 타이틀 업데이트
  const handleDatesSet = useCallback((arg) => {
    setCalendarTitle(arg.view.title);
    setCurrentViewType(arg.view.type);
    scheduleCapacityFullEventsRefresh(null, 160);
  }, [scheduleCapacityFullEventsRefresh]);

  const handleEventsSet = useCallback((events) => {
    scheduleCapacityFullEventsRefresh(events, 160);
  }, [scheduleCapacityFullEventsRefresh]);

  // 이벤트 커스텀 렌더러
  const renderEventContent = useCallback((eventInfo) => {
    if (eventInfo.view.type === 'dayGridMonth') {
      const isAllDay = eventInfo.event.allDay;
      const backgroundColor = eventInfo.event.backgroundColor || '#1976d2';
      const borderColor = eventInfo.event.borderColor || backgroundColor;

      return <CalendarMonthEventCircle allDay={isAllDay} backgroundColor={backgroundColor} borderColor={borderColor} />;
    }

    return <CalendarTimeGridEventContent title={eventInfo.event.title} />;
  }, []);

  // 30분 지점 점선 추가
  useEffect(() => {
    if (!isTimeGridView(currentViewType)) {
      clearScheduledDashedLinesUpdate();
      getCalendarElement()?.querySelectorAll('.dashed-time-line').forEach((line) => line.remove());
      return undefined;
    }

    scheduleDashedLinesUpdate(220);

    const handleResize = () => {
      scheduleDashedLinesUpdate(140);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearScheduledDashedLinesUpdate();
    };
  }, [clearScheduledDashedLinesUpdate, currentViewType, getCalendarElement, scheduleDashedLinesUpdate]);

  // 게스트 기간 포맷
  const formatGuestPeriod = () => {
    const startStr = guestStart.toFormat('yyyy.MM.dd');
    const endStr = guestEnd.toFormat('yyyy.MM.dd');
    return `${startStr} ~ ${endStr}`;
  };

  // 게스트 기간 외의 날에 회색 배경 적용 (월간 뷰)
  const dayCellDidMount = useCallback((info) => {
    const cellDate = DateTime.fromJSDate(info.date).setZone(DEFAULT_TIMEZONE).startOf('day');

    // 게스트 기간 외인지 확인
    if (cellDate < guestStart || cellDate > guestEnd) {
      // 회색 배경 적용 - !important 사용
      info.el.style.setProperty('background-color', 'rgba(210, 210, 210, 0.35)', 'important');
      info.el.style.setProperty('cursor', 'not-allowed', 'important');
      info.el.classList.add('outside-guest-period');
    }
  }, [guestStart, guestEnd]);

  // 주간/일간 뷰 요일 헤더에 스타일 적용
  const dayHeaderDidMount = useCallback((info) => {
    const headerDate = DateTime.fromJSDate(info.date).setZone(DEFAULT_TIMEZONE).startOf('day');

    // 게스트 기간 외인지 확인
    if (headerDate < guestStart || headerDate > guestEnd) {
      // 헤더 스타일 변경 (흐리게)
      info.el.style.setProperty('opacity', '0.5', 'important');
      info.el.style.setProperty('background-color', 'rgba(210, 210, 210, 0.25)', 'important');
      info.el.classList.add('outside-guest-period-header');
    }
  }, [guestStart, guestEnd]);


  return (
    <div className={styles.container}>
      {/* 게스트 기간 표시 */}
      <div className={styles.guestPeriodBadge}>
        <span className={styles.periodLabel}>{TEXT.guestPeriod[lang]}:</span>
        <span className={styles.periodValue}>{formatGuestPeriod()}</span>
      </div>

      {/* 캘린더 - GNEGNEGNE 스타일 적용 */}
      <div ref={calendarWrapperRef} className="calendar-wrapper guest-calendar-wrapper">
        {/* 커스텀 캘린더 헤더 */}
        <div className="calendar-custom-header">
          <div className="calendar-title-row">
            <button
              onClick={handlePrev}
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid #000',
                backgroundColor: '#fffdf8',
                cursor: 'pointer',
                borderRadius: '50%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
                outline: 'none'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M8 2L4 6L8 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="calendar-title-text" style={{ fontWeight: 300 }}>{calendarTitle}</span>
            <button
              onClick={handleNext}
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid #000',
                backgroundColor: '#fffdf8',
                cursor: 'pointer',
                borderRadius: '50%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
                outline: 'none'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          {/* 탭 네비게이션 */}
          <div style={{
            display: 'flex',
            marginBottom: '5px',
            gap: '0',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <div
              className="calendar-tab-container"
              style={{
                display: 'flex',
                gap: '0',
                justifyContent: 'center',
                border: '1px solid #000',
                borderRadius: '8px',
                padding: '0'
              }}
            >
              <button
                onClick={handleToday}
                style={{
                  padding: '4px 16px',
                  border: '1px solid transparent',
                  backgroundColor: '#fffdf8',
                  color: '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {TEXT.today[lang]}
              </button>
              <button
                onClick={() => handleChangeView('dayGridMonth')}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: '#fffdf8',
                  color: '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: currentViewType === 'dayGridMonth' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'dayGridMonth' ? '2' : '1'
                }}
              >
                {TEXT.month[lang]}
              </button>
              <button
                onClick={() => handleChangeView('timeGridWeek')}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: '#fffdf8',
                  color: '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: currentViewType === 'timeGridWeek' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'timeGridWeek' ? '2' : '1'
                }}
              >
                {TEXT.week[lang]}
              </button>
              <button
                onClick={() => handleChangeView('timeGridDay')}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'timeGridDay' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: '#fffdf8',
                  color: '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: currentViewType === 'timeGridDay' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'timeGridDay' ? '2' : '1'
                }}
              >
                {TEXT.day[lang]}
              </button>
            </div>
          </div>
        </div>

        {/* Safari 호환성을 위한 'Sandwich' 테두리 방식: 배경색으로 테두리 시뮬레이션 */}
        <div className="calendar-border-outer" style={{
          backgroundColor: '#000',
          padding: '1px',
          borderRadius: '10px',
          marginTop: '10px',
          overflow: 'hidden'
        }}>
          <div className="calendar-border-inner" style={{
            backgroundColor: '#fffdf8',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
              timeZone={DEFAULT_TIMEZONE}
              initialView="timeGridWeek"
              initialDate={initialCalendarDate.toJSDate()}
              validRange={{
                start: guestStart.toJSDate(),
                end: guestEnd.plus({ days: 1 }).toJSDate()
              }}
              headerToolbar={false}
              locales={[customFrLocale, customKoLocale, customEnLocale]}
              locale={getCalendarLocale(lang)}
              eventSources={calendarEventSources}
              lazyFetching={false}
              height="auto"
              slotMinTime="10:00:00"
              slotMaxTime="19:00:00"
              slotDuration="01:00:00"
              slotLabelInterval="01:00:00"
              snapDuration="00:30:00"
              slotEventOverlap={true}
              eventOrder={(a, b) => {
                // 홈스튜디오 이벤트는 먼저 렌더링 (뒤로), 게스트 이벤트는 나중에 (앞으로)
                const aIsBackground = a.extendedProps?.isBackground ? 0 : 1;
                const bIsBackground = b.extendedProps?.isBackground ? 0 : 1;
                return aIsBackground - bIsBackground;
              }}
              allDaySlot={true}
              editable={true}
              selectable={true}
              selectMirror={true}
              selectLongPressDelay={400}
              eventLongPressDelay={400}
              dateClick={handleDateClick}
              select={handleSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              datesSet={handleDatesSet}
              eventsSet={handleEventsSet}
              eventContent={renderEventContent}
              dayCellDidMount={dayCellDidMount}
              dayHeaderDidMount={dayHeaderDidMount}
            />
          </div>
        </div>
      </div>

      {/* 일정 추가 모달 */}
      {showAddModal && (
        <Suspense fallback={<GuestCalendarModalFallback lang={lang} />}>
          <GuestAddEventModal
            isOpen={showAddModal}
            selectedDate={selectedDate}
            selectedStartTime={selectedStartTime}
            selectedEndDate={selectedEndDate}
            selectedEndTime={selectedEndTime}
            onClose={() => setShowAddModal(false)}
            onEventAdded={handleEventAdded}
          />
        </Suspense>
      )}

      {/* 일정 수정 모달 */}
      {showEditModal && selectedEvent && (
        <Suspense fallback={<GuestCalendarModalFallback lang={lang} />}>
          <GuestEditEventModal
            isOpen={showEditModal}
            event={selectedEvent}
            onClose={() => {
              setShowEditModal(false);
              setSelectedEvent(null);
            }}
            onEventUpdated={handleEventUpdated}
          />
        </Suspense>
      )}
    </div>
  );
}
